/**
 * PRE Bundle Store — manages state for the single-file PRE bundle export/import
 * pipeline (validate → diff → apply).
 *
 * Talks to the super-admin API at /api/super-admin/pre/bundle/*. The validate
 * endpoint also returns counts and warnings; diff returns the full per-section
 * diff payload; apply runs the transactional reconcile (gated server-side by
 * PRE_BUNDLE_APPLY_ENABLED env flag).
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  PreBundle,
  PreBundleDiff,
} from '@respectly/shared-types';

export type BundleErrorSeverity = 'error' | 'warning';

export interface BundleErrorEntry {
  code: string;
  severity: BundleErrorSeverity;
  path: string | null;
  message: string;
  remediation?: string;
}

export interface ValidateResult {
  valid: true;
  bundleId: string;
  checksum: string;
  sourceEnv: string;
  generatedAt: string;
  counts: {
    jurisdictions: number;
    checkDefinitions: number;
    rules: number;
    callingHourWindows: number;
    dncRegistries: number;
    frequencyCaps: number;
    featureFlagDefaults: number;
  };
  warnings: BundleErrorEntry[];
}

export interface DiffResult {
  bundleId: string;
  checksum: string;
  sourceEnv: string;
  warnings: BundleErrorEntry[];
  diff: PreBundleDiff;
}

export interface ApplyResult {
  bundleId: string;
  checksum: string;
  rowsAffected: Record<string, { upserts?: number; deactivated?: number }>;
  durationMs: number;
}

interface BundleErrorPayload {
  code: string;
  message: string;
  errors?: BundleErrorEntry[];
}

interface PreBundleState {
  /** Raw uploaded bundle text (canonical JSON). */
  uploadedText: string | null;
  /** Parsed bundle object (returned from /validate). */
  parsedBundle: PreBundle | null;
  validateResult: ValidateResult | null;
  diffResult: DiffResult | null;
  applyResult: ApplyResult | null;

  isExporting: boolean;
  isValidating: boolean;
  isDiffing: boolean;
  isApplying: boolean;

  /** Last operation error (validation errors or HTTP/server errors). */
  lastError: BundleErrorPayload | null;

  /** Wizard step: 1 upload, 2 validate, 3 diff review, 4 confirm apply. */
  step: 1 | 2 | 3 | 4;

  // Actions
  reset: () => void;
  setUploadedText: (text: string) => void;
  setStep: (step: 1 | 2 | 3 | 4) => void;
  exportBundle: (opts?: { description?: string; releaseTag?: string; pretty?: boolean }) => Promise<void>;
  validate: () => Promise<boolean>;
  diff: () => Promise<boolean>;
  apply: (opts: {
    confirmChecksum: string;
    confirmSourceEnv: string;
    approverNotes: string;
    deactivateMissing: boolean;
  }) => Promise<boolean>;
}

function extractError(err: unknown): BundleErrorPayload {
  // ApiClientError exposes code/message/details; details may include `errors[]`.
  const e = err as {
    code?: string;
    message?: string;
    details?: { errors?: BundleErrorEntry[] };
  };
  return {
    code: e?.code ?? 'UNKNOWN_ERROR',
    message: e?.message ?? 'Unexpected error',
    errors: e?.details?.errors,
  };
}

export const usePreBundleStore = create<PreBundleState>((set, get) => ({
  uploadedText: null,
  parsedBundle: null,
  validateResult: null,
  diffResult: null,
  applyResult: null,
  isExporting: false,
  isValidating: false,
  isDiffing: false,
  isApplying: false,
  lastError: null,
  step: 1,

  reset: () =>
    set({
      uploadedText: null,
      parsedBundle: null,
      validateResult: null,
      diffResult: null,
      applyResult: null,
      lastError: null,
      step: 1,
    }),

  setUploadedText: (text) =>
    set({
      uploadedText: text,
      validateResult: null,
      diffResult: null,
      applyResult: null,
      parsedBundle: null,
      lastError: null,
      step: 2,
    }),

  setStep: (step) => set({ step }),

  exportBundle: async (opts) => {
    set({ isExporting: true, lastError: null });
    try {
      const api = getApiClient();
      const params: Record<string, string> = {};
      if (opts?.description) params.description = opts.description;
      if (opts?.releaseTag) params.releaseTag = opts.releaseTag;
      if (opts?.pretty) params.pretty = 'true';
      // Server returns canonical JSON as text/plain stream; request as text to
      // preserve byte-exact canonicalization (re-stringifying would break
      // checksum verification on download).
      const text = await api.get<string>(
        API_ENDPOINTS.SUPER_ADMIN.PRE.BUNDLE.EXPORT,
        params,
        { responseType: 'text' },
      );
      // Trigger browser download
      const blob = new Blob([text], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Filename derived from a peek inside the JSON
      let filename = 'pre-bundle.json';
      try {
        const parsed = JSON.parse(text) as PreBundle;
        const suffix = opts?.pretty ? '-pretty' : '';
        filename = `pre-bundle-${parsed.metadata.sourceEnv}-${parsed.integrity.checksum.slice(0, 8)}${suffix}.json`;
      } catch {
        /* fall back to default */
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      set({ lastError: extractError(err) });
    } finally {
      set({ isExporting: false });
    }
  },

  validate: async () => {
    const text = get().uploadedText;
    if (!text) {
      set({ lastError: { code: 'NO_BUNDLE', message: 'Upload a bundle first.' } });
      return false;
    }
    set({ isValidating: true, lastError: null, validateResult: null });
    try {
      const api = getApiClient();
      // Send the raw uploaded text. The api-client wraps body with
      // JSON.stringify when given a JS object, so we parse first to let it
      // re-serialize. The /validate endpoint accepts either string or object.
      const parsedJson = JSON.parse(text) as PreBundle;
      const result = await api.post<ValidateResult>(
        API_ENDPOINTS.SUPER_ADMIN.PRE.BUNDLE.VALIDATE,
        parsedJson,
      );
      set({ validateResult: result, parsedBundle: parsedJson, step: 3 });
      return true;
    } catch (err) {
      set({ lastError: extractError(err) });
      return false;
    } finally {
      set({ isValidating: false });
    }
  },

  diff: async () => {
    const bundle = get().parsedBundle;
    if (!bundle) {
      set({ lastError: { code: 'NO_BUNDLE', message: 'Validate the bundle first.' } });
      return false;
    }
    set({ isDiffing: true, lastError: null, diffResult: null });
    try {
      const api = getApiClient();
      const result = await api.post<DiffResult>(
        API_ENDPOINTS.SUPER_ADMIN.PRE.BUNDLE.DIFF,
        bundle,
      );
      set({ diffResult: result, step: 4 });
      return true;
    } catch (err) {
      set({ lastError: extractError(err) });
      return false;
    } finally {
      set({ isDiffing: false });
    }
  },

  apply: async (opts) => {
    const bundle = get().parsedBundle;
    if (!bundle) {
      set({ lastError: { code: 'NO_BUNDLE', message: 'Validate the bundle first.' } });
      return false;
    }
    set({ isApplying: true, lastError: null, applyResult: null });
    try {
      const api = getApiClient();
      const result = await api.post<ApplyResult>(
        API_ENDPOINTS.SUPER_ADMIN.PRE.BUNDLE.APPLY,
        {
          bundle,
          confirmChecksum: opts.confirmChecksum,
          confirmSourceEnv: opts.confirmSourceEnv,
          approverNotes: opts.approverNotes,
          deactivateMissing: opts.deactivateMissing,
        },
      );
      set({ applyResult: result });
      return true;
    } catch (err) {
      set({ lastError: extractError(err) });
      return false;
    } finally {
      set({ isApplying: false });
    }
  },
}));
